import React, { useState, useEffect } from "react";
import { Filter, Search, Scale, X, Tag, Check, Calendar, ChevronRight, Plus } from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_ICON } from "../styles/actionButtons";
import { Link, useNavigate } from "react-router-dom";
import LabelManager from "../components/LabelManager";
import LabelSelector from "../components/LabelSelector";
import LabelList from "../components/LabelList";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getUserLabels, assignLabelsToCase } from "../services/labelService";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";

// ── avatar helpers (for "Shared to") ──
const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
];
const colorFor = (seed) => {
  let h = 0;
  const s = seed || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

export default function SharedCases() {
  const navigate = useNavigate();
  const [allSharedCases, setAllSharedCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labels, setLabels] = useState([]);
  const [memberNames, setMemberNames] = useState({});
  const [memberPhotos, setMemberPhotos] = useState({});
  const [userTeams, setUserTeams] = useState([]);
  const { globalUser } = useAuth();

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [searchField, setSearchField] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [labelFilter, setLabelFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Label management state
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [showLabelSelector, setShowLabelSelector] = useState(false);
  const [selectedCaseForLabels, setSelectedCaseForLabels] = useState(null);
  const [showLabelList, setShowLabelList] = useState(false);
  const [isLabelAssignmentMode, setIsLabelAssignmentMode] = useState(false);
  const [selectedLabelForBulkAssign, setSelectedLabelForBulkAssign] = useState(null);
  const [selectedCasesForLabel, setSelectedCasesForLabel] = useState([]);

  const isUserAuthenticated = globalUser !== null;

  const getLabelById = (labelId) => labels.find((label) => label.id === labelId);

  const shouldShowUpdateBanner = (nextHearingDate, status) => {
    if (status?.toLowerCase() === "disposed") return false;
    if (!nextHearingDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [day, month, year] = nextHearingDate.split("/").map(Number);
    const hearingDate = new Date(year, month - 1, day);
    hearingDate.setHours(0, 0, 0, 0);
    return hearingDate <= today;
  };

  const getSharedToInfo = (caseItem) => {
    const caseTeam = caseItem.team || [];
    const ownerUid = caseItem.owner;
    const sharedUids = caseTeam.filter(
      (uid) => uid !== ownerUid && uid !== globalUser?.uid
    );
    const matchedTeamNames = [];
    const teamMemberUids = new Set();
    userTeams.forEach((team) => {
      const members = team.members || [];
      if (members.length > 0 && members.every((m) => caseTeam.includes(m))) {
        matchedTeamNames.push(team.teamName);
        members.forEach((m) => teamMemberUids.add(m));
      }
    });
    const individualMembers = sharedUids
      .filter((uid) => !teamMemberUids.has(uid))
      .map((uid) => ({ name: memberNames[uid] || "Unknown", profile: memberPhotos[uid] || "" }));
    return { teamNames: matchedTeamNames, individualMembers };
  };

  // Label handlers
  const handleOpenLabelSelector = (e, caseItem) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCaseForLabels(caseItem);
    setShowLabelSelector(true);
  };
  const handleLabelsAssigned = (caseId, labelIds) => {
    setAllSharedCases((prev) =>
      prev.map((c) => (c.id === caseId ? { ...c, labels: labelIds } : c))
    );
  };
  const handleLabelSelectForBulk = (label) => {
    setSelectedLabelForBulkAssign(label);
    setIsLabelAssignmentMode(true);
    setShowLabelList(false);
    setSelectedCasesForLabel([]);
  };
  const handleBulkLabelAssign = async () => {
    if (!selectedLabelForBulkAssign || selectedCasesForLabel.length === 0) return;
    try {
      const updatePromises = selectedCasesForLabel.map(async (caseItem) => {
        const currentLabels = caseItem.labels || [];
        const newLabels = currentLabels.includes(selectedLabelForBulkAssign.id)
          ? currentLabels
          : [...currentLabels, selectedLabelForBulkAssign.id];
        await assignLabelsToCase(caseItem.collection, caseItem.id, newLabels, globalUser?.uid);
        return { id: caseItem.id, labels: newLabels };
      });
      const results = await Promise.all(updatePromises);
      setAllSharedCases((prev) =>
        prev.map((c) => {
          const updated = results.find((r) => r.id === c.id);
          return updated ? { ...c, labels: updated.labels } : c;
        })
      );
      setIsLabelAssignmentMode(false);
      setSelectedLabelForBulkAssign(null);
      setSelectedCasesForLabel([]);
    } catch (err) {
      console.error("Error assigning labels:", err);
    }
  };
  const handleCancelBulkAssign = () => {
    setIsLabelAssignmentMode(false);
    setSelectedLabelForBulkAssign(null);
    setSelectedCasesForLabel([]);
  };

  // Fetch shared cases
  useEffect(() => {
    const fetchSharedCases = async () => {
      if (!isUserAuthenticated) return;
      try {
        const userId = globalUser.uid;
        const pendingCasesQuery = query(collection(db, "pending"), where("team", "array-contains", userId));
        const disposedCasesQuery = query(collection(db, "disposed"), where("team", "array-contains", userId));
        const [pendingSnapshot, disposedSnapshot] = await Promise.all([
          getDocs(pendingCasesQuery),
          getDocs(disposedCasesQuery),
        ]);
        const pendingCases = pendingSnapshot.docs.map((d) => ({ id: d.id, collection: "pending", ...d.data() }));
        const disposedCases = disposedSnapshot.docs.map((d) => ({ id: d.id, collection: "disposed", ...d.data() }));
        const cases = [...pendingCases, ...disposedCases].filter((c) => (c.team || []).length > 1);
        setAllSharedCases(cases);

        // Member names
        const allUids = new Set();
        cases.forEach((c) => (c.team || []).forEach((uid) => { if (uid !== userId) allUids.add(uid); }));
        const nameMap = {};
        const photoMap = {};
        await Promise.all(
          Array.from(allUids).map(async (uid) => {
            try {
              let snap = await getDoc(doc(db, "lawyers", uid));
              if (!snap.exists()) snap = await getDoc(doc(db, "client", uid));
              if (snap.exists()) {
                const d = snap.data();
                nameMap[uid] = d.name || d.displayName || "Unknown";
                photoMap[uid] = d.profile || ""; // Account page saves the pic to `profile`
              }
            } catch (err) {
              console.error("Error fetching user name:", err);
            }
          })
        );
        setMemberNames(nameMap);
        setMemberPhotos(photoMap);

        // Non-default owned teams (for "Shared to" team matching)
        const teamsSnapshot = await getDocs(query(collection(db, "teams"), where("ownerId", "==", userId)));
        setUserTeams(
          teamsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => !t.isDefault)
        );
      } catch (err) {
        console.error("Error fetching shared cases:", err);
        setError("Error fetching shared cases.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedCases();
  }, [globalUser, isUserAuthenticated]);

  // Fetch labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid) return;
      try {
        setLabels(await getUserLabels(globalUser.uid));
      } catch (err) {
        console.error("Error fetching labels:", err);
      }
    };
    fetchLabels();
  }, [globalUser]);

  // Filter cases
  useEffect(() => {
    let filtered = allSharedCases || [];
    if (statusFilter !== "all")
      filtered = filtered.filter((c) => c.status?.toLowerCase() === statusFilter.toLowerCase());
    if (labelFilter !== "all")
      filtered = filtered.filter((c) => {
        const userLabelIds = c.labelsByUser?.[globalUser?.uid] || c.labels || [];
        return userLabelIds.includes(labelFilter);
      });
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (searchField === "all")
        filtered = filtered.filter(
          (c) =>
            c.caseNo?.toLowerCase().includes(q) ||
            c.caseType?.toLowerCase().includes(q) ||
            c.petitionerName?.toLowerCase().includes(q) ||
            c.respondentName?.toLowerCase().includes(q) ||
            c.courtName?.toLowerCase().includes(q)
        );
      else if (searchField === "court") filtered = filtered.filter((c) => c.courtName?.toLowerCase().includes(q));
      else if (searchField === "name")
        filtered = filtered.filter(
          (c) => c.petitionerName?.toLowerCase().includes(q) || c.respondentName?.toLowerCase().includes(q)
        );
      else if (searchField === "petitioner") filtered = filtered.filter((c) => c.petitionerName?.toLowerCase().includes(q));
      else if (searchField === "respondent") filtered = filtered.filter((c) => c.respondentName?.toLowerCase().includes(q));
      else if (searchField === "caseNumber") filtered = filtered.filter((c) => c.caseNo?.toLowerCase().includes(q));
    }
    setFilteredCases(filtered);
    setCurrentPage(1);
  }, [searchQuery, allSharedCases, searchField, statusFilter, labelFilter, globalUser]);

  const searchPlaceholder =
    searchField === "all" ? "Search all fields..."
      : searchField === "court" ? "Search by court name..."
      : searchField === "name" ? "Search by party name..."
      : searchField === "petitioner" ? "Search by petitioner..."
      : searchField === "respondent" ? "Search by respondent..."
      : "Search by case number...";

  const SEARCH_CHIPS = [
    { value: "all", label: "All Fields" },
    { value: "court", label: "Court" },
    { value: "name", label: "Party Name" },
    { value: "petitioner", label: "Petitioner" },
    { value: "respondent", label: "Respondent" },
    { value: "caseNumber", label: "Case Number" },
  ];

  const total = filteredCases.length;
  const pageStart = total === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const pageEnd = Math.min(currentPage * ITEMS_PER_PAGE, total);
  const pageRows = filteredCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const toggleCaseForLabel = (caseItem) => {
    setSelectedCasesForLabel((prev) =>
      prev.some((c) => c.id === caseItem.id)
        ? prev.filter((c) => c.id !== caseItem.id)
        : [...prev, caseItem]
    );
  };

  const partyLine = (c) => {
    const p = (c.petitionerName || "").trim();
    const r = (c.respondentName || "").trim();
    if (p && r) return `${p} and ${r}`;
    return p || r || "—";
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Shared Cases</span>
        </div>

        {/* Header + actions */}
        <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Shared Cases</h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Cases that have been shared with you by your team members.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowLabelManager(true)}
              className={ACTION_BTN_GOLD}
            >
              <Plus className={ACTION_BTN_ICON} /> Add Category
            </button>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
              />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFilterOptions((v) => !v)}
                title="Filter"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
              >
                <Filter className="h-4 w-4" />
              </button>
              {showFilterOptions && (
                <div className="absolute right-0 z-30 mt-2 w-60 rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
                  <p className="mb-1.5 text-xs font-semibold text-slate-500">Status</p>
                  <div className="flex flex-wrap gap-1">
                    {["all", "pending", "disposed"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatusFilter(s)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors ${
                          statusFilter === s
                            ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <p className="mb-1.5 mt-3 text-xs font-semibold text-slate-500">Category</p>
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => setLabelFilter("all")}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        labelFilter === "all"
                          ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      All
                    </button>
                    {labels.map((label) => (
                      <button
                        key={label.id}
                        type="button"
                        onClick={() => setLabelFilter(label.id)}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium"
                        style={
                          labelFilter === label.id
                            ? { backgroundColor: label.color, color: "#ffffff" }
                            : { backgroundColor: "#f1f5f9", color: "#475569" }
                        }
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: label.color }} />
                        {label.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowLabelList((v) => !v)}
              title="Assign categories"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
            >
              <Tag className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search-by chips */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-500">Search by:</span>
          {SEARCH_CHIPS.map((chip) => {
            const active = searchField === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setSearchField(chip.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-300"
                    : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>

        {/* Label-assignment banner */}
        {isLabelAssignmentMode && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-medium text-yellow-800">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: selectedLabelForBulkAssign?.color }} />
              Assigning “{selectedLabelForBulkAssign?.name}” — select cards below ({selectedCasesForLabel.length})
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={selectedCasesForLabel.length === 0}
                onClick={handleBulkLabelAssign}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" /> Apply
              </button>
              <button
                type="button"
                onClick={handleCancelBulkAssign}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-red-600">{error}</div>
        )}

        {/* Empty */}
        {!loading && !error && allSharedCases.length === 0 && (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-20 text-center shadow-sm">
            <Scale className="mb-4 h-14 w-14 text-slate-300" />
            <p className="text-lg font-medium text-slate-700">Currently no shared cases</p>
            <p className="mt-1 text-sm text-slate-500">
              Cases shared with you by your team members will appear here.
            </p>
          </div>
        )}

        {/* No results after filter */}
        {!loading && !error && allSharedCases.length > 0 && total === 0 && (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-sm">
            <Search className="mb-3 h-10 w-10 text-slate-300" />
            <p className="text-base font-medium text-slate-700">No cases match your search.</p>
            <p className="mt-1 text-sm text-slate-500">Try a different search or filter.</p>
          </div>
        )}

        {/* Cards */}
        {!loading && !error && total > 0 && (
          <>
            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {pageRows.map((c) => {
                const isDisposed = c.status?.toLowerCase() === "disposed";
                const hearingSet = Boolean(c.nextHearingDate);
                const userLabelIds = c.labelsByUser?.[globalUser?.uid] || c.labels || [];
                const caseLabels = userLabelIds.map(getLabelById).filter(Boolean);
                const { teamNames, individualMembers } = getSharedToInfo(c);
                const shares = [
                  ...individualMembers,
                  ...teamNames.map((n) => ({ name: n, profile: "" })),
                ];
                const shownShares = shares.slice(0, 4);
                const extraShares = shares.length - shownShares.length;
                const selected = selectedCasesForLabel.some((s) => s.id === c.id);

                const onCardClick = () => {
                  if (isLabelAssignmentMode) toggleCaseForLabel(c);
                  else navigate(`/portal/case/${c.id}?from=shared`);
                };

                return (
                  <div
                    key={c.id}
                    onClick={onCardClick}
                    className={`group flex cursor-pointer flex-col rounded-xl border bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md ${
                      selected ? "border-yellow-400 ring-2 ring-yellow-400/40" : "border-slate-200"
                    }`}
                  >
                    <div className="flex gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow-50 text-yellow-500">
                        {isLabelAssignmentMode ? (
                          <input
                            type="checkbox"
                            checked={selected}
                            readOnly
                            className="h-4 w-4 accent-yellow-500"
                          />
                        ) : (
                          <Scale className="h-4 w-4" />
                        )}
                      </span>
                      <div className="min-w-0 flex-grow">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[11px] text-slate-400">Case Number: {c.caseNo || "—"}</p>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {caseLabels.slice(0, 1).map((label) => (
                              <span
                                key={label.id}
                                className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                                style={{ backgroundColor: label.color, color: "#ffffff" }}
                              >
                                {label.name}
                              </span>
                            ))}
                            {!isDisposed && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/portal/case/${c.id}?from=shared`);
                                }}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
                              >
                                <Calendar className="h-3 w-3" /> Update Next Hearing
                              </button>
                            )}
                          </div>
                        </div>
                        <h3 className="truncate text-sm font-bold text-slate-900" title={c.caseType}>
                          {c.caseType || "Case"}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-[11px] text-slate-600">
                          <span className="font-medium text-slate-500">Party: </span>
                          {partyLine(c)}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-slate-600" title={c.courtName || ""}>
                          <span className="font-medium text-slate-500">Court: </span>
                          {c.courtName || "—"}
                        </p>
                        {!isDisposed && (
                          <p className={`mt-0.5 text-[11px] font-medium ${hearingSet ? "text-blue-600" : "text-orange-500"}`}>
                            Next Hearing Date: {c.nextHearingDate || "Not Set"}
                          </p>
                        )}
                        <p className={`mt-0.5 text-[11px] font-medium ${isDisposed ? "text-rose-500" : "text-green-600"}`}>
                          Status: {c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase() : "—"}
                        </p>

                        {shares.length > 0 && (
                          <div className="mt-2">
                            <p className="text-[10px] text-slate-400">Shared to:</p>
                            <div className="mt-1 flex items-center -space-x-1.5">
                              {shownShares.map((m, i) =>
                                m.profile ? (
                                  <img
                                    key={`${m.name}-${i}`}
                                    src={m.profile}
                                    alt={m.name}
                                    title={m.name}
                                    className="h-6 w-6 rounded-full object-cover ring-2 ring-white"
                                  />
                                ) : (
                                  <span
                                    key={`${m.name}-${i}`}
                                    title={m.name}
                                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold ring-2 ring-white ${colorFor(m.name)}`}
                                  >
                                    {initialsOf(m.name)}
                                  </span>
                                )
                              )}
                              {extraShares > 0 && (
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[9px] font-semibold text-slate-600 ring-2 ring-white">
                                  +{extraShares}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-sm text-slate-500">
                Showing {pageStart} to {pageEnd} of {total} shared case{total === 1 ? "" : "s"}
              </p>
              <Pagination
                currentPage={currentPage}
                totalItems={total}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          </>
        )}
      </main>

      {/* Modals */}
      <LabelManager
        isOpen={showLabelManager}
        onClose={() => setShowLabelManager(false)}
        labels={labels}
        userId={globalUser?.uid}
        onLabelsUpdate={setLabels}
      />
      {selectedCaseForLabels && (
        <LabelSelector
          isOpen={showLabelSelector}
          onClose={() => {
            setShowLabelSelector(false);
            setSelectedCaseForLabels(null);
          }}
          caseItem={selectedCaseForLabels}
          allLabels={labels}
          onLabelsAssigned={handleLabelsAssigned}
          userId={globalUser?.uid}
        />
      )}
      <LabelList
        isOpen={showLabelList}
        onClose={() => setShowLabelList(false)}
        labels={labels}
        onLabelSelect={handleLabelSelectForBulk}
      />
    </div>
  );
}
